import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { AutomatedBadge, isAutomatedMetric } from './AutomationBadge';

const MetricsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [metrics, setMetrics] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    source: '',
    frequency: '',
    aggregation_type: '',
    data_type: '',
    unit: '',
    display_format: '',
    higher_is_better: true,
    responsible_id: '',
    department_id: '',
    area_id: '',
    company_id: '',
    is_active: 1,
    sort_order: 0,
    created_by: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Check for edit query parameter and open edit form
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && metrics.length > 0) {
      const metricToEdit = metrics.find(m => m.id === parseInt(editId));
      if (metricToEdit) {
        // Set editing metric and populate form (inline handleEdit logic)
        setEditingMetric(metricToEdit);
        setFormData({
          name: metricToEdit.name || '',
          description: metricToEdit.description || '',
          source: metricToEdit.source || '',
          frequency: metricToEdit.frequency ? metricToEdit.frequency.toString() : '',
          aggregation_type: metricToEdit.aggregation_type || '',
          data_type: metricToEdit.data_type || '',
          unit: metricToEdit.unit || '',
          display_format: metricToEdit.display_format || '',
          higher_is_better: metricToEdit.higher_is_better !== false, // Default to true if not set
          responsible_id: metricToEdit.responsible_id ? metricToEdit.responsible_id.toString() : '',
          department_id: metricToEdit.department_id ? metricToEdit.department_id.toString() : '',
          area_id: metricToEdit.area_id ? metricToEdit.area_id.toString() : '',
          company_id: '1', // Default to company 1 when editing
          is_active: metricToEdit.is_active || 1,
          sort_order: metricToEdit.sort_order || 0,
          created_by: metricToEdit.created_by || 1
        });
        setShowForm(true);
        // Remove the query parameter from URL
        setSearchParams({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, metrics]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [metricsRes, companiesRes, departmentsRes, areasRes, peopleRes] = await Promise.all([
        apiService.metrics.getAll(),
        apiService.companies.getAll(),
        apiService.departments.getAll(),
        apiService.areas.getAll(),
        apiService.people.getAll()
      ]);
      setMetrics(metricsRes.data);
      setCompanies(companiesRes.data);
      setDepartments(departmentsRes.data);
      setAreas(areasRes.data);
      setPeople(peopleRes.data);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        frequency: formData.frequency ? parseInt(formData.frequency) : null,
        responsible_id: formData.responsible_id ? parseInt(formData.responsible_id) : null,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        area_id: formData.area_id ? parseInt(formData.area_id) : null,
        company_id: editingMetric ? 1 : parseInt(formData.company_id), // Default to 1 when editing
        is_active: parseInt(formData.is_active),
        sort_order: parseInt(formData.sort_order),
        display_format: formData.display_format || null,
        higher_is_better: formData.higher_is_better !== false // Default to true
      };

      if (editingMetric) {
        await apiService.metrics.update(editingMetric.id, {
          ...dataToSubmit,
          updated_by: 1
        });
      } else {
        await apiService.metrics.create(dataToSubmit);
      }
      
      setShowForm(false);
      setEditingMetric(null);
      resetFormData();
      fetchData();
    } catch (err) {
      setError('Failed to save metric');
      console.error('Error saving metric:', err);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      source: '',
      frequency: '',
      aggregation_type: '',
      data_type: '',
      unit: '',
      display_format: '',
      higher_is_better: true,
      responsible_id: '',
      department_id: '',
      area_id: '',
      company_id: '',
      is_active: 1,
      sort_order: 0,
      created_by: 1
    });
  };

  const handleEdit = (metric) => {
    setEditingMetric(metric);
    setFormData({
      name: metric.name || '',
      description: metric.description || '',
      source: metric.source || '',
      frequency: metric.frequency ? metric.frequency.toString() : '',
      aggregation_type: metric.aggregation_type || '',
      data_type: metric.data_type || '',
      unit: metric.unit || '',
      display_format: metric.display_format || '',
      higher_is_better: metric.higher_is_better !== false, // Default to true if not set
      responsible_id: metric.responsible_id ? metric.responsible_id.toString() : '',
      department_id: metric.department_id ? metric.department_id.toString() : '',
      area_id: metric.area_id ? metric.area_id.toString() : '',
      company_id: '1', // Default to company 1 when editing
      is_active: metric.is_active || 1,
      sort_order: metric.sort_order || 0,
      created_by: metric.created_by || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this metric?')) {
      try {
        await apiService.metrics.delete(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete metric');
        console.error('Error deleting metric:', err);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingMetric(null);
    resetFormData();
  };

  const getName = (items, id) => {
    if (!id) return 'Not assigned';
    const item = items.find(i => i.id === id);
    return item ? item.name : 'Unknown';
  };

  if (loading) return <div className="loading">Loading metrics...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Metrics</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Add Metric
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="form">
          <h3>{editingMetric ? 'Edit Metric' : 'Add New Metric'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Metric Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              {!editingMetric && (
                <div className="form-group">
                  <label className="form-label">Company *</label>
                  <select
                    className="form-control"
                    value={formData.company_id}
                    onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                    required
                  >
                    <option value="">Select a company</option>
                    {companies.map(comp => (
                      <option key={comp.id} value={comp.id}>
                        {comp.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Source</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  placeholder="e.g., CRM System, Survey Platform"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Frequency (days)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.frequency}
                  onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                  placeholder="e.g., 1 for daily, 7 for weekly"
                />
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Aggregation Type</label>
                <select
                  className="form-control"
                  value={formData.aggregation_type}
                  onChange={(e) => setFormData({...formData, aggregation_type: e.target.value})}
                >
                  <option value="">Select type</option>
                  <option value="sum">Sum</option>
                  <option value="average">Average</option>
                  <option value="count">Count</option>
                  <option value="min">Minimum</option>
                  <option value="max">Maximum</option>
                  <option value="cumulative">Cumulative</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Data Type</label>
                <select
                  className="form-control"
                  value={formData.data_type}
                  onChange={(e) => setFormData({...formData, data_type: e.target.value})}
                >
                  <option value="">Select type</option>
                  <option value="numeric">Numeric</option>
                  <option value="text">Text</option>
                  <option value="percentage">Percentage</option>
                  <option value="currency">Currency</option>
                </select>
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Unit</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.unit}
                  onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  placeholder="e.g., €, $, %, score, tickets"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Display Format</label>
                <select
                  className="form-control"
                  value={formData.display_format}
                  onChange={(e) => setFormData({...formData, display_format: e.target.value})}
                >
                  <option value="">Default (number)</option>
                  <option value="currency_abbreviated">Currency Abbreviated (e.g., 1.345k €)</option>
                  <option value="currency">Currency (e.g., 1,345.34 €)</option>
                  <option value="number_abbreviated">Number Abbreviated (e.g., 1.345k)</option>
                  <option value="number">Number (standard)</option>
                  <option value="percentage">Percentage (e.g., 85.5%)</option>
                  <option value="score">Score (0-100, e.g., 85)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Target Direction</label>
                <select
                  className="form-control"
                  value={formData.higher_is_better ? 'true' : 'false'}
                  onChange={(e) => setFormData({...formData, higher_is_better: e.target.value === 'true'})}
                >
                  <option value="true">Higher is Better (default)</option>
                  <option value="false">Lower is Better (e.g., work at risk)</option>
                </select>
                <small className="text-muted">Determines if values above or below target are considered good</small>
              </div>
              <div className="form-group">
                <label className="form-label">Responsible Person</label>
                <select
                  className="form-control"
                  value={formData.responsible_id}
                  onChange={(e) => setFormData({...formData, responsible_id: e.target.value})}
                >
                  <option value="">Select person</option>
                  {people
                    .filter(person => person.company_id.toString() === (editingMetric ? '1' : formData.company_id))
                    .map(person => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Department</label>
                <select
                  className="form-control"
                  value={formData.department_id}
                  onChange={(e) => {
                    const newDepartmentId = e.target.value;
                    // Clear area_id if the current area doesn't belong to the new department
                    const currentArea = areas.find(a => a.id.toString() === formData.area_id);
                    const newAreaId = currentArea && currentArea.department_id.toString() === newDepartmentId 
                      ? formData.area_id 
                      : '';
                    setFormData({...formData, department_id: newDepartmentId, area_id: newAreaId});
                  }}
                >
                  <option value="">Select department</option>
                  {departments
                    .filter(dept => dept.company_id.toString() === (editingMetric ? '1' : formData.company_id))
                    .map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Area</label>
                <select
                  className="form-control"
                  value={formData.area_id}
                  onChange={(e) => setFormData({...formData, area_id: e.target.value})}
                  disabled={!formData.department_id}
                >
                  <option value="">{formData.department_id ? 'Select area' : 'Select department first'}</option>
                  {areas
                    .filter(area => {
                      // Filter by department_id if selected, otherwise by company_id
                      const companyId = editingMetric ? '1' : formData.company_id;
                      if (formData.department_id) {
                        return area.department_id.toString() === formData.department_id;
                      }
                      return area.company_id.toString() === companyId;
                    })
                    .map(area => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: parseInt(e.target.value)})}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sort Order</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                {editingMetric ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Company</th>
              <th>Department</th>
              <th>Responsible</th>
              <th>Data Type</th>
              <th>Unit</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map(metric => (
              <tr key={metric.id}>
                <td>{metric.id}</td>
                <td>
                  <Link 
                    to={`/metrics/${metric.id}`}
                    style={{color: '#034955', textDecoration: 'none', fontWeight: '500'}}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {metric.name}
                    {isAutomatedMetric(metric) && <AutomatedBadge />}
                  </Link>
                </td>
                <td>{getName(companies, metric.company_id)}</td>
                <td>{getName(departments, metric.department_id)}</td>
                <td>{getName(people, metric.responsible_id)}</td>
                <td>{metric.data_type || 'Not set'}</td>
                <td>{metric.unit || 'No unit'}</td>
                <td>
                  <span style={{
                    color: metric.is_active ? 'green' : 'red',
                    fontWeight: 'bold'
                  }}>
                    {metric.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <Link 
                    to={`/metrics/${metric.id}`}
                    className="btn btn-primary btn-small"
                    style={{marginRight: '0.5rem'}}
                  >
                    Details
                  </Link>
                  <button 
                    className="btn btn-secondary btn-small" 
                    onClick={() => handleEdit(metric)}
                    style={{marginRight: '0.5rem'}}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-small" 
                    onClick={() => handleDelete(metric.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {metrics.length === 0 && (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
            No metrics found. Add one to get started!
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsList;